import { useEffect, useState } from 'react';
import {
  buildQueryFilters,
  buildQuerySortingFromStateArray,
  mapColumnFiltersToCrudFilters,
  SpendBrainTableName,
  VectorContractDto,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { DataTable, useTableSettings } from '@sib/table';
import { Button, TableWrapper } from '@sib/ui';
import { NoVendors } from '@sib/ui/icons';
import { NoTableData } from '@spendbrain/common';
import { useDialogStore } from '@spendbrain/common/stores';
import { useTablePreferencesSettings } from '@spendbrain/hooks';
import { useAppTranslation } from '@spendbrain/modules/vendors/hooks';
import { ContractPreview } from './ContractPreview';
import { ContractPreviewDialog } from './ContractPreviewDialog';
import {
  ContractPreview as ContractPreviewType,
  ContractScope,
  useContracts,
} from './ContractsTable.utils';
import { useContractsColumns } from './ContractsTable.utils';

export interface ContractsTableProps {
  vendorId?: string;
  scope?: ContractScope;
  showCloseButton?: boolean;
}

export const ContractsTable = ({
  vendorId,
  scope = 'vendor',
  showCloseButton = true,
}: ContractsTableProps) => {
  const { t } = useAppTranslation();
  const { close } = useDialogStore();

  const {
    onColumnVisibilityChange,
    onColumnPinningChange,
    state,
    disabledColumns,
  } = useTablePreferencesSettings(SpendBrainTableName.Contracts);
  const [contractPreview, setContractPreview] =
    useState<ContractPreviewType | null>(null);
  const organizationId = useUserSetupStore(state => state.organization.id);
  const columns = useContractsColumns({ setContractPreview });

  const tableSettings = useTableSettings<VectorContractDto>({
    tableId: SpendBrainTableName.Contracts,
    sorting: true,
    ...state,
    onColumnVisibilityChange,
    onColumnPinningChange,
    disabledColumns,
    defaultColumnPinningState: {
      left: [],
      right: ['actions'],
    },
    columnPinning: state?.columnPinning,
  });
  const { pagination, columnFilters, sorting, resetPagination } = tableSettings;

  useEffect(() => resetPagination(), [organizationId, resetPagination]);

  const mappedColumnFilters = mapColumnFiltersToCrudFilters(
    columnFilters,
    columns
  );
  const { data, isLoading, rowCount } = useContracts({
    organizationId,
    vendorId,
    scope,
    page: pagination.pageIndex,
    size: pagination.pageSize,
    sort: buildQuerySortingFromStateArray(sorting),
    filter: buildQueryFilters(mappedColumnFilters),
  });

  return (
    <>
      {!contractPreview || scope === 'organization' ? (
        <TableWrapper className="flex-col items-center gap-2">
          <DataTable
            TablePaginationProps={{
              itemsPerPageLabel: t('components.tablePagination.itemsPerPage'),
            }}
            withSettingsRow
            loading={isLoading}
            columns={columns}
            rowCount={rowCount}
            data={data}
            settings={tableSettings}
            preserveSelectedValues
            TableContainerProps={{
              className: 'flex-[1_1] flex flex-col items-stretch',
            }}
            noResultsPlaceholder={
              tableSettings.hasFiltersSet ? (
                <NoTableData
                  title={t('noFilteredResults.title')}
                  description={t('noFilteredResults.description')}
                  className="py-10"
                />
              ) : (
                <NoTableData
                  title={t('components.contracts.noData.title')}
                  description={t('components.contracts.noData.description')}
                  className="py-10"
                  Icon={NoVendors}
                />
              )
            }
          />
        </TableWrapper>
      ) : (
        <ContractPreview
          fileUrl={contractPreview.fileUrl}
          title={contractPreview?.title || ''}
        />
      )}
      {scope === 'organization' && (
        <ContractPreviewDialog
          contractPreview={contractPreview}
          onClose={() => setContractPreview(null)}
        />
      )}
      {(showCloseButton || (scope === 'vendor' && contractPreview)) && (
        <Button
          onClick={() => (contractPreview ? setContractPreview(null) : close())}
          variant="outline"
          className="mx-auto mt-2 w-16"
        >
          {contractPreview ? t('back') : t('close')}
        </Button>
      )}
    </>
  );
};
